// @ts-check
type BaseProduct = {
  __typename: 'Wine' | 'Beer' | 'Spirit' | 'Cider' | 'Product';
  shortName: string;
  countryCode?: string;
  country?: string;
  countryName?: string;
  denomination?: string;
  classification?: string;
};

type Wine = BaseProduct & {
  __typename: 'Wine' | 'Product';
  specialClassification?: string;
  region?: string;
  subregion?: string;
  vineyard?: string;
  vintage?: string;
};

type Beer = BaseProduct & {
  __typename: 'Beer';
};

type Spirit = BaseProduct & {
  __typename: 'Beer';
};

type Product = Wine | Beer | Spirit;

export function originPrefix(product: Product) {
  // Non-Austrian products get the country on the name
  if (!isAustrianWine(product)) {
    const origin = [
      product.country || product.countryName,
      product.denomination,
    ]
      .filter(Boolean)
      .join(' | ');
    const wine = getWine(product);
    if (!wine) return origin;
    return [origin, wine.specialClassification].filter(Boolean).join(' | ');
  }

  // Sekt Austria has nameLine2 and 3 only.
  if (isSektAustria(product)) return '';
  // Österreichischer Sekt has nameLine2 and 3 only.
  if (isAustrianSekt(product)) return '';

  const origin = [
    product?.denomination,
    classifications[product?.classification || ''],
  ]
    .filter(Boolean)
    .join(' ');
  if (origin.trim() === product.shortName.trim()) return '';
  return origin;
}
const classifications = {
  'DAC Große Reserve': 'Große Reserve',
  'DAC Reserve': 'Reserve',
  'DAC rot': 'rot',
  'DAC weiß': 'weiß',
  'DAC Rosé': 'Rosé',
};

export function nameWithoutOrigin(product: Product) {
  const origin = originPrefix(product);

  // Sekt Austria
  if (isSektAustria(product)) {
    const protectedOrigin = originSuffix(product);
    const name = product.shortName.trim();
    if (protectedOrigin && name.endsWith(protectedOrigin)) {
      return name.slice(0, -protectedOrigin.length).trim();
    }
  }
  // Wine
  if (!origin) return product?.shortName;
  if (origin && product?.shortName.startsWith(`${origin} `)) {
    const nameLine2 = product?.shortName?.replace(origin, '').trim();
    if (nameLine2) return nameLine2;
  }
  return product?.shortName;
}

function getWine(product: Product) {
  if (product.__typename === 'Wine') return product;
  // Product is the type we get from useEventData.
  if (product.__typename === 'Product') return product;
  return undefined;
}

export function originSuffix(product: Product) {
  if (isSektAustria(product)) {
    const wine = getWine(product);
    if (!wine?.region) return '';
    if (wine.region === 'Bergland')
      return `${wine.subregion} g.U. ${wine.vineyard || ''}`.trim();
    return `${sektAustriaOrigins[wine.region] || wine.region} g.U. ${
      wine.vineyard || ''
    }`.trim();
  }
  return '';
}

const sektAustriaOrigins = {
  'Lower Austria': 'Niederösterreich',
  Vienna: 'Wien',
};

function isSektAustria(product: Product) {
  if (!isAustrianWine(product)) return false;
  if ((product.countryCode !== undefined && product.countryCode) !== 'at') {
    return false;
  }
  if (!getWine(product)) return false;
  if (product?.classification?.startsWith('Sekt Austria')) return true;
  if (product?.shortName.startsWith('Sekt Austria ')) return true;
}

function isAustrianSekt(product: Product) {
  if (!isAustrianWine(product)) return false;
  if (product?.classification === 'Österreichischer Sekt') return true;
  if (product?.shortName.startsWith('Österreichischer Sekt ')) return true;
  return false;
}

function isAustrianWine(product: Product) {
  if (product.countryCode !== undefined && product.countryCode !== 'at') {
    return false;
  }
  if (!getWine(product)) return false;
  return true;
}
